import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Menu, Search } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { AnimatePresence } from 'framer-motion'

import { Sidebar } from '@/components/chat/Sidebar'
import { HomeView } from '@/components/chat/HomeView'
import { ChatArea } from '@/components/chat/ChatArea'
import { ContextBuilderView } from '@/components/chat/ContextBuilderView'
import { ContextDashboard } from '@/components/chat/ContextDashboard'
import { UploadsLibraryView } from '@/components/chat/UploadsLibraryView'
import { DocumentDetail } from '@/components/chat/DocumentDetail'
import { CommandPalette } from '@/components/chat/CommandPalette'
import { ArtifactPickerDialog } from '@/components/chat/ArtifactPickerDialog'
import type { SidebarTab, ExplorerDoc } from '@/components/chat/types'
import { useChatSession } from '@/hooks/useChatSession'
import { useArtifactGeneration } from '@/hooks/useArtifactGeneration'
import { useDocumentUpload } from '@/hooks/useDocumentUpload'
import { documentService } from '@/services'
import { useAppStore } from '@/stores/appStore'
import type { ArtifactType, Document } from '@/types/api'

/* ================================================================
   ChatPage — unified shell for the chat-first redesign.
   Desktop: 240px sidebar + main content
   Mobile:  Sheet slide-over + header bar
   ================================================================ */

export function ChatPage() {
  const { sessionId } = useParams<{ sessionId?: string }>()
  const startupId = useAppStore((s) => s.startup?.id)

  // ── Navigation state ────────────────────────────────────────
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('home')
  const [selectedDoc, setSelectedDoc] = useState<ExplorerDoc | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [artifactPickerOpen, setArtifactPickerOpen] = useState(false)
  const [voiceCaptureRequestId, setVoiceCaptureRequestId] = useState(0)
  const [contextView, setContextView] = useState<'overview' | 'uploads'>('overview')
  const [documents, setDocuments] = useState<Document[]>([])
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [documentsError, setDocumentsError] = useState<string | null>(null)

  // ── Chat session (hook handles SSE, messaging, state) ───────
  const {
    session, messages, gaps, inputValue, setInputValue, loading, agentState,
    scrollRef, loadSession, startSession, sendMessage, resetChat,
  } = useChatSession()
  const upload = useDocumentUpload()

  // ── Artifact generation ─────────────────────────────────────
  const { generations, generate, dismiss } = useArtifactGeneration()

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true)
    setDocumentsError(null)

    try {
      const response = await documentService.getDocuments()
      setDocuments(Array.isArray(response?.documents) ? response.documents : [])
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load uploads.'
      setDocumentsError(message)
    } finally {
      setDocumentsLoading(false)
    }
  }, [])

  const handleGenerateArtifact = useCallback(
    async (type: ArtifactType) => {
      setArtifactPickerOpen(false)
      setSidebarTab('chats')
      await generate(type)
    },
    [generate]
  )

  // ── Force "chats" tab when a sessionId is in the URL ────────
  useEffect(() => {
    if (sessionId) {
      setSidebarTab('chats')
      loadSession(sessionId)
    }
  }, [sessionId, loadSession])

  // ── Auto-start session when landing on /chat without a sessionId ──
  useEffect(() => {
    if (!sessionId && !session && !loading) {
      startSession()
    }
  }, [sessionId, session, loading, startSession])

  useEffect(() => {
    void loadDocuments()
  }, [loadDocuments])

  useEffect(() => {
    if (sidebarTab === 'context') {
      void loadDocuments()
    }
  }, [sidebarTab, loadDocuments])

  // ── Keyboard shortcut: Cmd+K → search ──────────────────────
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setSearchOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  // ── Messaging: route to startSession or sendMessage ─────────
  const handleSend = useCallback(
    async (text: string = inputValue) => {
      if (!text.trim()) return
      setSidebarTab('chats')
      setInputValue('')
      if (!session) {
        await startSession(text)
      } else {
        await sendMessage(text)
      }
    },
    [inputValue, session, startSession, sendMessage, setInputValue]
  )

  // ── New chat ────────────────────────────────────────────────
  const handleNewChat = useCallback(() => {
    resetChat()
    setSidebarTab('chats')
  }, [resetChat])

  const handleSidebarTabChange = useCallback((tab: SidebarTab) => {
    setSidebarTab(tab)
    if (tab !== 'context') {
      setContextView('overview')
    }
  }, [])

  // ── Action handlers for sub-views ──────────────────────────
  const handleHomeAction = useCallback(
    (action: string) => {
      if (action === 'upload') upload.triggerFilePicker()
      else if (action === 'interview') handleSend('Tell me about my startup')
      else if (action === 'generate') setArtifactPickerOpen(true)
    },
    [handleSend, upload.triggerFilePicker]
  )

  const requestVoiceCapture = useCallback(() => {
    setSidebarTab('chats')
    setVoiceCaptureRequestId((prev) => prev + 1)
  }, [])

  const handleSelectUpload = useCallback((doc: ExplorerDoc) => {
    setSelectedDoc(doc)
  }, [])

  const handleOpenAllUploads = useCallback(() => {
    setContextView('uploads')
  }, [])

  const handleBackToOverview = useCallback(() => {
    setContextView('overview')
  }, [])

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className="flex h-full w-full bg-background overflow-hidden text-foreground selection:bg-primary/20">
      <input
        ref={upload.fileInputRef}
        type="file"
        accept=".pdf,.pptx,.txt,.md,.csv,.mp3"
        className="hidden"
        onChange={(e) => upload.handleFileChange(e)}
        title="Upload documents"
        aria-label="Upload documents"
      />

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
      <ArtifactPickerDialog
        open={artifactPickerOpen}
        onOpenChange={setArtifactPickerOpen}
        onSelect={handleGenerateArtifact}
      />

      {/* Desktop Sidebar */}
      <div className="hidden md:block w-[240px] flex-shrink-0">
        <Sidebar
          activeTab={sidebarTab}
          onTabChange={handleSidebarTabChange}
          onSelectDoc={setSelectedDoc}
          onNewChat={handleNewChat}
          documents={documents}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Document Detail Overlay */}
        <AnimatePresence>
          {selectedDoc && (
            <div className="absolute inset-0 z-50 flex">
              <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedDoc(null)} />
              <DocumentDetail doc={selectedDoc} onClose={() => setSelectedDoc(null)} startupId={startupId} />
            </div>
          )}
        </AnimatePresence>

        {/* Header */}
        <header className="h-14 border-b border-border/50 flex items-center justify-between px-4 bg-background/50 backdrop-blur-md sticky top-0 z-10 shrink-0">
          <div className="flex items-center gap-2">
            {/* Mobile sidebar trigger */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden -ml-2">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[280px] border-r-white/15 bg-secondary">
                <Sidebar
                  activeTab={sidebarTab}
                  onTabChange={handleSidebarTabChange}
                  onSelectDoc={setSelectedDoc}
                  onNewChat={handleNewChat}
                  documents={documents}
                />
              </SheetContent>
            </Sheet>

            <span className="font-medium text-sm md:hidden">
              {sidebarTab === 'home' ? 'Home' : sidebarTab === 'context' ? 'Context Explorer' : 'Chat'}
            </span>

            {/* Desktop search bar */}
            <div className="hidden md:flex items-center relative w-full max-w-md ml-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                className="w-full rounded-lg py-1.5 pl-9 pr-4 text-sm outline-none transition-all border border-[#dce3f1] bg-[#f7f9fe] hover:bg-[#f1f5ff] focus:border-[#9bb5ff] text-[#475569] placeholder:text-[#7c8aa5] dark:border-[#454a6d] dark:bg-[#23263b] dark:hover:bg-[#2b2f4a] dark:focus:border-[#5f79df] dark:text-[#d5dbef] dark:placeholder:text-[#9aa5c7]"
                placeholder="Search documents, insights, chats... (Cmd+K)"
                onClick={() => setSearchOpen(true)}
                readOnly
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="md:hidden text-muted-foreground hover:text-foreground" onClick={() => setSearchOpen(true)}>
              <Search className="h-5 w-5" />
            </Button>
          </div>
        </header>

        {/* View Switcher */}
        {sidebarTab === 'home' ? (
          <HomeView
            onStartAction={handleHomeAction}
            onUploadClick={upload.triggerFilePicker}
            onVoiceClick={requestVoiceCapture}
          />
        ) : sidebarTab === 'context' ? (
          contextView === 'overview' ? (
            <ContextDashboard
              onNavigateToChat={() => setSidebarTab('chats')}
              documents={documents}
              documentsLoading={documentsLoading}
              documentsError={documentsError}
              onSelectUpload={handleSelectUpload}
              onSeeAllUploads={handleOpenAllUploads}
            />
          ) : (
            <UploadsLibraryView
              documents={documents}
              loading={documentsLoading}
              error={documentsError}
              onBack={handleBackToOverview}
              onRefresh={loadDocuments}
              onSelectUpload={handleSelectUpload}
            />
          )
        ) : sidebarTab === 'add-context' ? (
          <ContextBuilderView
            onAction={(action, label) =>
              handleSend(
                `${action === 'upload' ? 'I want to upload' : action === 'research' ? 'Run agent:' : ''} ${label}`
              )
            }
            onRecordAudio={requestVoiceCapture}
          />
        ) : (
          <ChatArea
            messages={messages}
            gaps={gaps}
            inputValue={inputValue}
            loading={loading}
            agentState={agentState}
            scrollRef={scrollRef}
            onInputChange={setInputValue}
            onSend={handleSend}
            onUploadClick={upload.triggerFilePicker}
            voiceCaptureRequestId={voiceCaptureRequestId}
            onSelectCategory={(cat) =>
              handleSend(`Let's fill my ${cat} context gaps. Ask me targeted questions one by one.`)
            }
            artifactGenerations={generations}
            onDismissArtifact={dismiss}
          />
        )}
      </div>
    </div>
  )
}
